// Copyright 2019 Sorama B.V.

import {
    bindable,
    BindingEngine,
    bindingMode,
    computedFrom,
    customElement,
    Disposable,
    observable,
} from "aurelia-framework";
import { GenericMeasurement } from "datatypes/SoramaWebApi";
import { IPagedRequest } from "libraries/WebRequestHandler/IWebRequestHandler";
import { CloudImageService } from "services/Image/CloudImageService";
import { IImageService, Image, ImageStorageType, NewImage } from "services/Image/IImageService";
import { LocalImageService } from "services/Image/LocalImageService";
import { IUserSettingsService } from "services/UserManagement/IUserSettingsService";

interface ImageOrderMode {
    name: string;
    command: string;
    icon: string;
}

class ImageOrderModes {
    public static alphabeticalAscending: ImageOrderMode = {
        name: "Alphabetical Ascending",
        command: "$orderby=name asc",
        icon: "fas fa-sort-alpha-down",
    };
    public static alphabeticalDescending: ImageOrderMode = {
        name: "Alphabetical Descending",
        command: "$orderby=name desc",
        icon: "fas fa-sort-alpha-up",
    };
    public static uploadDateAscending: ImageOrderMode = {
        name: "Upload Date Ascending",
        command: "$orderby=created asc",
        icon: "fas fa-sort-numeric-down",
    };
    public static uploadDateDescending: ImageOrderMode = {
        name: "Upload Date Descending",
        command: "$orderby=created desc",
        icon: "fas fa-sort-numeric-up",
    };

    public static all: ImageOrderMode[] = [
        ImageOrderModes.alphabeticalAscending,
        ImageOrderModes.alphabeticalDescending,
        ImageOrderModes.uploadDateAscending,
        ImageOrderModes.uploadDateDescending,
    ];
}

@customElement("image-picker")
export class ImagePicker {
    private static readonly UserSettingsGroupKey = "ImagePicker";

    private imagePickerContainer: HTMLElement;
    private scrollContainer: HTMLElement;
    private dropAreaElement: HTMLElement;

    private readonly imageOrderModes: ImageOrderModes[] = ImageOrderModes.all;
    private readonly localStorageMode = ImageStorageType.Local;
    private readonly cloudStorageMode = ImageStorageType.Cloud;

    private disposables: Disposable[] = [];

    private imagesRequest: IPagedRequest<Image>;
    private requestPromise: Promise<unknown>;
    private deletePromise: Promise<void>;
    private scrollUpdateTimeout: number;

    @computedFrom("storageMode")
    private get imageService(): IImageService {
        switch (this.storageMode) {
            case this.localStorageMode:
                return this.localImageService;

            case this.cloudStorageMode:
                return this.cloudImageService;

            default:
                throw new Error("Invalid storage mode");
        }
    }

    protected dragOver: boolean;
    protected busyLoading: boolean;
    protected uploading: boolean;
    protected deleting: boolean;

    protected newImage: string;
    protected newImageName: string;
    protected newImageDescription: string;

    @observable
    protected imageOrderMode = ImageOrderModes.uploadDateDescending;

    @observable
    protected storageMode: ImageStorageType;

    @observable
    protected selectedFile: FileList;

    @observable
    protected zoomLevel = 4;

    @computedFrom("zoomLevel", "imagePickerContainer.offsetWidth")
    protected get imageSize(): number {
        if (!this.imagePickerContainer) return 0;

        return Math.floor((1 / this.zoomLevel) * this.imagePickerContainer.offsetWidth);
    }

    @computedFrom("scrollContainer.scrollHeight", "scrollContainer.scrollTop", "scrollContainer.offsetHeight")
    protected get scrollPercentage(): number {
        if (!this.scrollContainer || this.scrollContainer.scrollHeight === 0) return 1;

        return (this.scrollContainer.scrollTop + this.scrollContainer.offsetHeight) / this.scrollContainer.scrollHeight;
    }

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public image: Image;

    @bindable
    public measurement?: GenericMeasurement;

    /** Indicates that the image picker allows the user to upload and select a locally stored image */
    @bindable
    public allowLocalStorage = false;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public lastUploadedImageId?: string;

    constructor(
        private readonly cloudImageService: CloudImageService,
        private readonly localImageService: LocalImageService,
        private readonly bindingEngine: BindingEngine,
        private readonly userSettingsService: IUserSettingsService
    ) {}

    public async bind(): Promise<void> {
        const userSettingsDisposable = await this.userSettingsService.bind(
            ImagePicker.UserSettingsGroupKey,
            "storageMode",
            this
        );
        const scrollPercentageDisposable = this.bindingEngine
            .propertyObserver(this, "scrollPercentage")
            .subscribe(this.scrollPercentageChanged);

        this.disposables.push(userSettingsDisposable, scrollPercentageDisposable);

        if (!this.storageMode || (!this.allowLocalStorage && this.storageMode === this.localStorageMode)) {
            this.storageMode = this.cloudStorageMode;
        }
    }

    public attached(): void {
        this.dropAreaElement.addEventListener("dragenter", this.onDragEnter, false);
        this.dropAreaElement.addEventListener("dragleave", this.onDragLeave, false);
        this.dropAreaElement.addEventListener("dragover", this.onDragEnter, false);
        this.dropAreaElement.addEventListener("drop", this.onDrop, false);
    }

    public detached(): void {
        this.dropAreaElement.removeEventListener("dragenter", this.onDragEnter, false);
        this.dropAreaElement.removeEventListener("dragleave", this.onDragLeave, false);
        this.dropAreaElement.removeEventListener("dragover", this.onDragEnter, false);
        this.dropAreaElement.removeEventListener("drop", this.onDrop, false);
    }

    public unbind(): void {
        while (this.disposables.length) this.disposables.pop().dispose();
    }

    private refresh(): void {
        const promise = this.requestPromise ? this.requestPromise : Promise.resolve();

        // Update the request
        promise.then(() => {
            this.imagesRequest = this.imageService.getImages(this.imageOrderMode.command);
            this.loadNext();
        });
    }

    //#region Change handlers

    protected zoomLevelChanged(): void {
        this.scrollSelectedImageIntoView();
    }

    protected measurementChanged(): void {
        // Update query string
        this.refresh();
    }

    protected imageChanged(): void {
        this.scrollSelectedImageIntoView();
    }

    protected scrollPercentageChanged = () => {
        this.loadNext();
    };

    protected selectedFileChanged(): void {
        if (!this.selectedFile) return;

        const file = this.selectedFile.length > 0 ? this.selectedFile[0] : undefined;
        this.selectedFile = undefined;

        if (file) this.openImage(file);
    }

    protected storageModeChanged(): void {
        this.refresh();
    }

    //#endregion

    //#region Mouse/Touch handlers

    private onDragEnter = (e): void => {
        e.stopPropagation();
        e.preventDefault();

        this.dragOver = true;
    };

    private onDragLeave = (e): void => {
        e.stopPropagation();
        e.preventDefault();

        this.dragOver = false;
    };

    private onDrop = (e: DragEvent): void => {
        e.stopPropagation();
        e.preventDefault();

        this.dragOver = false;

        // Upload file
        const file = e.dataTransfer.files && e.dataTransfer.files.length > 0 ? e.dataTransfer.files[0] : undefined;
        if (!file) return;

        if (!file.type.startsWith("image/")) return;

        this.openImage(file);
    };

    //#endregion

    private openImage(file: File) {
        let busyLoading = true;
        const fileBlob = file.slice();
        const reader = new FileReader();

        const readerPromise = new Promise<string>((resolve, reject) => {
            reader.onloadend = () => {
                resolve(<string>reader.result);
            };

            reader.onerror = (e) => {
                reject(e);
            };
        });
        reader.readAsDataURL(fileBlob);

        readerPromise
            .then((image) => {
                this.newImageName = file.name;
                this.newImageDescription = undefined;
                this.newImage = image;
            })
            .catch((e) => {
                console.error(e);
            })
            .then(() => {
                busyLoading = false;
            });
    }

    protected uploadImage = (): void => {
        this.busyLoading = true;
        this.uploading = true;

        this.imageService
            .createImage(<NewImage>{
                name: this.newImageName,
                description: this.newImageDescription,
                data: this.newImage,
            })
            .then((addedImage) => {
                this.newImage = undefined;
                this.newImageName = undefined;
                this.newImageDescription = undefined;

                this.lastUploadedImageId = addedImage.id;
                this.refresh();
            })
            .catch((e) => {
                console.error("Error uploading image", e);
            })
            .then(() => {
                this.busyLoading = false;
                this.uploading = false;
            });
    };

    protected selectImage = (image: Image): void => {
        if (this.deleting) {
            const promise = this.deletePromise ? this.deletePromise : Promise.resolve();

            this.deletePromise = promise.then(() => {
                return this.imageService
                    .deleteImage(image.id)
                    .then(() => {
                        const index = this.imagesRequest.total.findIndex((x) => x.id === image.id);
                        this.imagesRequest.total.splice(index, 1);

                        if (this.image.id === image.id) {
                            this.image = undefined;
                        }
                    })
                    .catch((e) => {
                        console.error(e);
                    });
            });
        } else {
            this.image = this.image && this.image.id === image.id ? undefined : image;
        }
    };

    protected toggleDeleteMode = (): void => {
        this.deleting = !this.deleting;
    };

    protected toggleStorageMode = (): void => {
        this.storageMode = this.storageMode === this.localStorageMode ? this.cloudStorageMode : this.localStorageMode;
    };

    private scrollSelectedImageIntoView(): void {
        if (!this.imagesRequest || !this.imagePickerContainer) return;

        if (this.scrollUpdateTimeout) {
            window.clearTimeout(this.scrollUpdateTimeout);
            this.scrollUpdateTimeout = undefined;
        }

        // Find image in list
        const idx = this.imagesRequest.total.findIndex((x) => x === this.image);

        if (idx === -1) return;

        const element = this.imagePickerContainer.children[idx];

        if (element) {
            this.scrollUpdateTimeout = window.setTimeout(() => {
                element.scrollIntoView(false);
                this.scrollUpdateTimeout = undefined;
            }, 250);
        }
    }

    private loadNext(): void {
        if (this.imagesRequest.finished || this.requestPromise) return;

        if ((this.scrollPercentage > 0.8 || this.scrollContainer.scrollHeight > 900) && this.scrollContainer) {
            const offsetTop = this.scrollContainer.scrollTop;

            this.requestPromise = this.imagesRequest
                .next()
                .then(() => {
                    // Reset scrolltop to prevent the list from jumping back up
                    this.requestPromise = undefined;
                    this.scrollContainer.scrollTop = Math.max(offsetTop, this.scrollContainer.scrollTop);
                    this.loadNext();
                })
                .catch((e) => {
                    console.error(e);
                    this.requestPromise = undefined;
                });
        }
    }

    private floor(num: number): number {
        return Math.floor(num);
    }

    private ceil(num: number): number {
        return Math.ceil(num);
    }

    private imageOrderModeChanged(): void {
        if (this.imagesRequest) {
            this.refresh();
        }
    }
}
